import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';

function PasswordResetPage({ navigateTo, goBack }) {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  // Detect mobile/desktop
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (!email) {
      alert('Please enter your email address');
      return;
    }

    // Basic email validation
    if (!email.includes('@')) {
      alert('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password-confirm`
      });

      if (error) {
        console.error('Error sending reset email:', error);
        alert(`Failed to send reset email: ${error.message}`);
      } else {
        setResetSent(true);
      }
    } catch (error) {
      console.error('Error sending reset email:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
      padding: isMobile ? '20px 16px' : '40px 20px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      {/* Back Button - Always Visible */}
      <button
        onClick={() => navigateTo('login')}
        style={{
          position: 'absolute',
          top: isMobile ? '20px' : '32px',
          left: isMobile ? '16px' : '32px',
          background: 'rgba(255, 255, 255, 0.2)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          color: 'white',
          width: '40px',
          height: '40px',
          borderRadius: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          fontSize: '20px',
          transition: 'all 0.2s ease'
        }}
        onMouseEnter={(e) => {
          e.target.style.background = 'rgba(255, 255, 255, 0.3)';
        }}
        onMouseLeave={(e) => {
          e.target.style.background = 'rgba(255, 255, 255, 0.2)';
        }}
      >
        ←
      </button>

      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: isMobile ? '40px 28px' : '48px 40px',
        width: '100%',
        maxWidth: '440px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.4)'
      }}>
        {!resetSent ? (
          <>
            {/* Header */}
            <div style={{
              textAlign: 'center',
              marginBottom: '32px'
            }}>
              <h1 style={{
                fontSize: '28px',
                fontWeight: '800',
                color: '#1F2937',
                margin: '0 0 8px 0',
                letterSpacing: '-0.5px'
              }}>
                Reset Password
              </h1>
              <p style={{
                fontSize: '15px',
                color: '#6B7280',
                margin: '0',
                lineHeight: '1.5'
              }}>
                Enter your email and we'll send you a link to reset your password.
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleResetPassword}>
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#1F2937',
                  marginBottom: '8px'
                }}>
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  disabled={isSubmitting}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    border: '2px solid #E5E7EB',
                    borderRadius: '10px',
                    fontSize: '15px',
                    fontFamily: 'inherit',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                    boxSizing: 'border-box',
                    background: isSubmitting ? '#F9FAFB' : 'white'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#FF8C00';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#E5E7EB';
                  }}
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                style={{
                  width: '100%',
                  padding: '14px',
                  background: isSubmitting ? '#9CA3AF' : '#FF8C00',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '16px',
                  fontWeight: '700',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  boxShadow: isSubmitting ? 'none' : '0 4px 12px rgba(255, 140, 0, 0.4)',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitting) {
                    e.target.style.background = '#E67E00';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSubmitting) {
                    e.target.style.background = '#FF8C00';
                  }
                }}
              >
                {isSubmitting ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>

            {/* Back to Login Link */}
            <div style={{
              textAlign: 'center',
              marginTop: '24px'
            }}>
              <button
                onClick={() => navigateTo('login')}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#2563EB',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
              >
                Back to Login
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Success Message */}
            <div style={{
              textAlign: 'center'
            }}>
              <div style={{
                width: '64px',
                height: '64px',
                background: '#D1FAE5',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px',
                fontSize: '32px'
              }}>
                ✓
              </div>

              <h2 style={{
                fontSize: '24px',
                fontWeight: '800',
                color: '#1F2937',
                margin: '0 0 12px 0'
              }}>
                Password Reset Sent
              </h2>

              <p style={{
                fontSize: '15px',
                color: '#4B5563',
                lineHeight: '1.6',
                margin: '0 0 32px 0'
              }}>
                We've sent a password reset link to <strong>{email}</strong>
                <br /><br />
                Check your email and click the link to reset your password. The link will expire in 1 hour.
              </p>

              <button
                onClick={() => navigateTo('login')}
                style={{
                  width: '100%',
                  padding: '14px',
                  background: '#FF8C00',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '16px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(255, 140, 0, 0.4)',
                  marginBottom: '12px'
                }}
              >
                Back to Login
              </button>

              <button
                onClick={() => {
                  setResetSent(false);
                  setEmail('');
                }}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: 'transparent',
                  color: '#6B7280',
                  border: '2px solid #E5E7EB',
                  borderRadius: '10px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Resend Link
              </button>
            </div>
          </>
        )}
      </div>

      {/* Help Text */}
      {!resetSent && (
        <div style={{
          maxWidth: '440px',
          width: '100%',
          textAlign: 'center',
          marginTop: '24px',
          color: 'rgba(255, 255, 255, 0.8)',
          fontSize: '14px',
          lineHeight: '1.5'
        }}>
          If you don't receive an email, check your spam folder or try again with the email you used to register.
        </div>
      )}
    </div>
  );
}

export default PasswordResetPage;
